import React, { forwardRef, useMemo, useState } from "react"
import VirtualActivityList from "./calculator/VirtualActivityList"
import CalculatorDisplay from "./calculator/calculator-display"
import { activitiesData } from "@/lib/activities-data"
import type { Activity, ActivityCategory } from "@/lib/types"

const groupActivitiesByCategory = (activities: Activity[]): ActivityCategory[] => {
  const grouped: { [key: string]: Activity[] } = {}
  activities.forEach((activity) => {
    if (!grouped[activity.category]) {
      grouped[activity.category] = []
    }
    grouped[activity.category].push(activity)
  })
  return Object.entries(grouped).map(([name, activities]) => ({ name, activities }))
}

const CalculatorSection = forwardRef<HTMLDivElement>((_props, ref) => {
  const [selectedActivities, setSelectedActivities] = useState<Set<string>>(new Set())

  const categories = useMemo(() => groupActivitiesByCategory(activitiesData), [])

  const handleActivityToggle = (activityId: string, checked: boolean) => {
    setSelectedActivities((prev) => {
      const newSet = new Set(prev)
      if (checked) {
        newSet.add(activityId)
      } else {
        newSet.delete(activityId)
      }
      return newSet
    })
  }

  const totalHoursSaved = useMemo(() => {
    return Array.from(selectedActivities).reduce((sum, id) => {
      const activity = activitiesData.find((a) => a.id === id)
      return sum + (activity ? activity.timeSaved : 0)
    }, 0)
  }, [selectedActivities])

  return (
    <section ref={ref} id="calculator" className="py-16 md:py-24 bg-brand-dark relative z-10">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12 md:mb-16">
          Calculate Your Time-Saving Potential
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 md:gap-12">
          <div className="md:col-span-1 lg:col-span-3">
            <VirtualActivityList
              categories={categories}
              selectedActivities={selectedActivities}
              onActivityToggle={handleActivityToggle}
            />
          </div>
          <div className="md:col-span-1 lg:col-span-2">
            <CalculatorDisplay totalHoursSaved={totalHoursSaved} />
          </div>
        </div>
      </div>
    </section>
  )
})

CalculatorSection.displayName = "CalculatorSection"
export default CalculatorSection
